<template>
  <div
    v-if="loadingDetail"
    class="eu-pa_6 eu-align_center"
  >
    <emd-loader :type="'_invert'" />
  </div>
  <div v-else>
    <div class="el-section">
      <div class="el-grid-list">
        <div class="emd-container">
          <div
            class="emd-status-bar"
            v-bind:class="statusClass(webhook.status)"
          ></div>
          <div class="emd-container__content">
            <div class="el-chunk">
              <h4 class="emd-text _color_subtle _type_label _size_tiny">
                {{ $t('webhook.info.status') }}
              </h4>
            </div>
            <span
              class="emd-badge"
              v-bind:class="statusClass(webhook.status)"
              >{{ webhook.status }}</span
            >
          </div>
        </div>
        <div class="emd-container">
          <div class="emd-container__content">
            <div class="el-chunk">
              <h4 class="emd-text _color_subtle _type_label _size_tiny">
                {{ $t('webhook.info.event') }}
              </h4>
            </div>
            <p class="emd-text _size_section">{{ webhook.event }}</p>
          </div>
        </div>
        <div class="emd-container">
          <div class="emd-container__content">
            <div class="el-chunk">
              <h4 class="emd-text _color_subtle _type_label _size_tiny">
                {{ $t('webhook.info.last-attempt') }}
              </h4>
            </div>
            <p class="emd-text _size_section">
              {{ webhook.last_attempt | dateFormat('dateAndHour') }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="el-section">
      <div class="emd-container _highlight_header">
        <div class="emd-container__header">
          <h2 class="emd-text _size_section">{{ $t('webhook.info.title') }}</h2>
          <button
            class="emd-btn _color_primary _size_small"
            v-on:click="openModal"
            v-if="
              !retryDisabled(webhook.status) &&
              permissionIsEnabled('webhook:attempt')
            "
          >
            <i
              class="uil uil-sync"
              transform="width-16"
            />
            {{ $t('webhook.info.resend-webhook') }}
          </button>
        </div>
        <div
          v-if="showAlert"
          class="emd-container__content"
        >
          <emd-alert
            :variant="formError ? 'danger' : 'success'"
            @cancel="showAlert = false"
            :title="
              formError ? $t('alert.error-title') : $t('alert.success-title')
            "
          >
            <template slot="content">
              <div v-if="formError">
                <p
                  v-for="(error, index) in errors"
                  :key="index"
                >
                  {{ error }}
                </p>
              </div>
              <p v-else>{{ $t('webhook.info.success-resend') }}</p>
            </template>
          </emd-alert>
        </div>
        <div class="emd-container__content">
          <div class="emd-panel">
            <div class="emd-panel__section">
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <h2 class="emd-text _color_default _type_caption">
                    {{ $t('webhook.info.detail') }}
                  </h2>
                </div>
              </div>
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('webhook.info.id') }}
                  </p>
                  <span>{{ webhook.id || '-' }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('webhook.info.event-name') }}
                  </p>
                  <span>{{ webhook.event_name || '-' }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('webhook.info.event-type') }}
                  </p>
                  <span>{{ webhook.event || '-' }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('webhook.info.description') }}
                  </p>
                  <span>{{ webhook.description || '-' }}</span>
                </div>
                <div
                  class="el-chunk"
                  v-if="webhook.object"
                >
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('webhook.info.origin-id') }}
                  </p>
                  <span>{{ webhook.object.id || '-' }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('webhook.info.next-attempt') }}
                  </p>
                  <span>{{
                    webhook.next_attempt | dateFormat('dateAndHour')
                  }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('webhook.info.last-attempt') }}
                  </p>
                  <span>{{
                    webhook.last_attempt | dateFormat('dateAndHour')
                  }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('webhook.info.made-attempts') }}
                  </p>
                  <span>{{ attemptsMade }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('webhook.info.total-attempts') }}
                  </p>
                  <span>{{ totalAttempts }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('webhook.info.created-at') }}
                  </p>
                  <span>{{
                    webhook.created_at | dateFormat('dateAndHour')
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <log-links
      :entity="webhook"
      :title="$t('log-links.webhook')"
    ></log-links>
    <modal-password
      v-if="modalOpenned"
      @cancel="closeModal"
      :callback="retryWebhook"
    ></modal-password>
  </div>
</template>
<script>
import { statusClass } from '@/filters'
import { EmdLoader, EmdAlert } from 'emerald-vue/src/components'
import ModalPassword from '@/components/project/globals/ModalPassword.vue'
import LogLinks from '@/components/project/molecules/LogLinks/index.vue'
export default {
  name: 'WebhookInfo',
  components: {
    EmdLoader,
    EmdAlert,
    ModalPassword,
    LogLinks
  },
  data () {
    return {
      statusClass: statusClass,
      webhook: {},
      loadingDetail: true,
      modalOpenned: false,
      showAlert: false,
      formError: false,
      errors: []
    }
  },
  computed: {
    params () {
      return {
        merchantId: this.$store.getters.merchantId,
        accountId: this.$store.getters.accountId,
        webhookId: this.$route.params.id,
        entityId: this.$route.params.id,
        refresh: true
      }
    },
    totalAttempts () {
      return this.webhook.attempts ? this.webhook.attempts.split('/')[1] : '-'
    },
    attemptsMade () {
      return this.webhook.attempts ? this.webhook.attempts.split('/')[0] : '-'
    }
  },
  methods: {
    getWebhook () {
      this.params.query = {
        fields:
          'id,status,event,event_name,description,created_at,attempts,next_attempt,last_attempt,object{id,type,parent_id,parent_type}'
      }
      this.$store.dispatch('GET_WEBHOOK', this.params).then(res => {
        this.loadingDetail = false
        this.webhook = res
      })
    },
    retryDisabled (status) {
      status = status.toLowerCase()
      return status === 'pending' || status === 'pendente'
    },
    openModal () {
      if (this.retryDisabled(this.webhook.status)) {
        return
      }
      this.modalOpenned = true
    },
    closeModal () {
      this.modalOpenned = false
      this.selectedWebhooks = []
    },
    retryWebhook (password) {
      const body = {
        webhook_ids: [this.webhook.id],
        password: password
      }
      this.errors = []
      const msgErrorInternal = this.$t('webhook.info.error-internal')
      return this.$store
        .dispatch('RETRY_WEBHOOK', { params: this.params, body: body })
        .then(res => {
          this.showAlert = true
          this.formError = false
          this.getWebhook()
          this.$store.dispatch('GET_LAST_LOG', this.params)
        })
        .catch(err => {
          this.closeModal()
          this.showAlert = true
          this.formError = true
          if (err.body && Array.isArray(err.body.errors)) {
            err.body.errors.forEach(error => {
              if (!this.errors.includes(error.message)) {
                this.errors.push(error.message)
              }
            })
          } else {
            this.errors.push(msgErrorInternal)
          }
          window.scrollTo(0, 0)
        })
    },
    permissionIsEnabled (action) {
      return this.$store.getters.permissionIsEnabled(action)
    }
  },
  created () {
    this.getWebhook()
  }
}
</script>
